import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
    Autocomplete,
    CircularProgress,
    Box,
    TextField,
    Avatar,
    Typography,
    Stack,
    IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { baseApi, organizationsApi, processesApi } from "api";
import useSnackbar from "hooks/useSnackbar";
import { useTranslations } from "hooks";
import { getTranslation } from "common/utilities";

export interface SubstituteUser {
    id: number;
    firstname: string;
    lastname: string;
    picture?: string | null;
}

interface SubstitutesMenuProps {
    idOrganization: number;
    idProcess: number;
    idToken: number;
    currentUser?: SubstituteUser | null;
    onAssignmentChange?: (newUser: SubstituteUser | null) => void;
    isEditing?: boolean;
    onEditClick?: (e: React.MouseEvent) => void;
    onlySelect?: boolean;
    history?: boolean;
}

const SubstitutesMenu: React.FC<SubstitutesMenuProps> = ({
    idOrganization,
    idProcess,
    idToken,
    currentUser,
    onAssignmentChange,
    isEditing,
    onEditClick,
    onlySelect,
    history
}) => {
    const [users, setUsers] = useState<SubstituteUser[]>([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const { sendSnack } = useSnackbar();
    const translations = useTranslations();

    const fetchSubstitutes = useCallback(async () => {
        setLoading(true);
        try {
            const response = await baseApi.get(`/users/organizations/${idOrganization}/processes/${idProcess}/working-swimlanes`);
            const fetchedUsers = response.data.users || [];
            setUsers(fetchedUsers);
            // console.log("Working swimlanes users:", fetchedUsers);
        } catch (error) {
            console.error("Error fetching substitutes:", error);
            sendSnack({
                message: getTranslation(translations, "generic.error"),
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    }, [idOrganization, idProcess, sendSnack, translations]);

    useEffect(() => {
        if (open) {
            fetchSubstitutes();
        }
    }, [open, fetchSubstitutes]);

    useEffect(() => {
        if (isEditing) {
            setOpen(true);
        }
    }, [isEditing]);

    const handleAssign = async (user: SubstituteUser | null) => {
        if (!user) return;

        if (onlySelect) {
            if (onAssignmentChange) {
                onAssignmentChange(user);
            }
            setOpen(false);
            return;
        }

        try {
            await processesApi.put(`/tokens/${idToken}`, {
                assigned_to: user.id,
            });
            sendSnack({
                message: getTranslation(translations, "generic.success"),
                type: "success",
            });
            if (onAssignmentChange) {
                onAssignmentChange(user);
            }
            setOpen(false);
        } catch (error) {
            console.error("Error updating assignment:", error);
            sendSnack({
                message: getTranslation(translations, "generic.error"),
                type: "error",
            });
        }
    };

    const currentValue = useMemo(() => {
        if (!currentUser || currentUser.id <= 0) return null;
        return users.find(u => u.id === currentUser.id) || null;
    }, [currentUser, users]);

    const isLoading = loading;

    if (!isEditing) {
        const displayUser = currentUser && currentUser.id > 0 ? currentUser : null;
        return (
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
            >
                {displayUser && (
                    <>
                        <Avatar
                            src={displayUser.picture || undefined}
                            sx={{ width: 24, height: 24, fontSize: 12 }}
                        >
                            {displayUser.firstname?.[0]}{displayUser.lastname?.[0]}
                        </Avatar>
                        <Typography>
                            {displayUser.firstname} {displayUser.lastname}
                        </Typography>
                    </>
                )}
                {!history && (
                    <IconButton
                        id="edit-participant-btn"
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onEditClick) onEditClick(e);
                        }}
                        sx={{ ml: 0.5, p: 0.5 }}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>)}
            </Stack>
        );
    }

    return (
        <Box
            id="substitutes-autocomplete-container"
            onClick={(e) => {
                e.stopPropagation();
            }}>
            <Autocomplete
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                value={currentValue}
                onChange={(e, newValue) => {
                    e.stopPropagation();
                    handleAssign(newValue);
                }}
                options={users}
                loading={isLoading}
                loadingText={getTranslation(translations, "generic.loading") || "Caricamento..."}
                noOptionsText="Nessun utente trovato"
                getOptionLabel={(option) => `${option.firstname} ${option.lastname}`.trim()}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                size="small"
                sx={{ minWidth: 200, maxWidth: 300 }}
                // MUI native search is enabled by default - no filterOptions override
                PaperComponent={(props) => (
                    <Box
                        {...props}
                        sx={{
                            maxHeight: 300,
                            overflow: 'auto',
                            backgroundColor: '#ffffff',
                            boxShadow: '0px 5px 15px rgba(0,0,0,0.2)',
                            zIndex: 1300,
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                        }}
                    />
                )}
                renderOption={(props, option) => {
                    const optionKey = `option-${option.id}`;
                    return (
                        <Box
                            component="li"
                            {...props}
                            key={optionKey}
                            sx={{ py: 0.5, px: 1 }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Avatar
                                    src={option.picture || undefined}
                                    sx={{ width: 32, height: 32, fontSize: 14 }}
                                >
                                    {option.firstname?.[0]}{option.lastname?.[0]}
                                </Avatar>
                                <Typography>
                                    {option.firstname} {option.lastname}
                                </Typography>
                            </Stack>
                        </Box>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="Cerca utente..."
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: currentValue ? (
                                <React.Fragment>
                                    <Avatar
                                        src={currentValue.picture || undefined}
                                        sx={{ width: 24, height: 24, mr: 1, fontSize: 12 }}
                                    >
                                        {currentValue.firstname?.[0]}{currentValue.lastname?.[0]}
                                    </Avatar>
                                    {params.InputProps.startAdornment}
                                </React.Fragment>
                            ) : (
                                params.InputProps.startAdornment
                            ),
                            endAdornment: (
                                <React.Fragment>
                                    {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </React.Fragment>
                            ),
                        }}
                    />
                )}
            />
        </Box>
    );
};

export default SubstitutesMenu;
